<template>
    <div class="RecollectionIntervalCriterion">
        <CriterionSettingsRow class="py-2.5">
            <p class="RecollectionIntervalCriterion__description">
                {{ description }}
            </p>
            <!--
                Sits above the mode selector rather than inside the cadence branch: the criterion is
                invalid until a mode is chosen, and an error rendered only in cadence mode could never
                report that.
            -->
            <div
                v-if="visibleError"
                class="RecollectionIntervalCriterion__error"
                role="alert"
            >
                {{ visibleError }}
            </div>
            <RbacDisableGuard
                v-slot="{ isDisabledByRbac }"
                :features="[RbacFeature.decisioningManageCriteria]"
            >
                <div class="RecollectionIntervalCriterion__modes">
                    <div
                        v-for="modeOption in modeOptions"
                        :key="modeOption.value"
                        class="RecollectionIntervalCriterion__mode"
                    >
                        <RadioOption
                            :name="modeRadioGroupName"
                            :value="modeOption.value"
                            :checked="mode === modeOption.value"
                            :disabled="isDisabledByRbac"
                            @input="onModeInput(modeOption.value)"
                        >
                            {{ modeOption.label }}
                        </RadioOption>
                        <p class="RecollectionIntervalCriterion__modeDescription">
                            {{ modeOption.description }}
                        </p>
                    </div>
                </div>
            </RbacDisableGuard>
            <template v-if="isRecurringCadence && valueProperty && unitProperty">
                <RbacDisableGuard
                    v-slot="{ isDisabledByRbac }"
                    :features="[RbacFeature.decisioningManageCriteria]"
                >
                    <div
                        class="RecollectionIntervalCriterion__inputs"
                        :class="{
                            'RecollectionIntervalCriterion__inputs--invalid': Boolean(visibleError),
                        }"
                    >
                        <CriterionNumberInput
                            class="RecollectionIntervalCriterion__valueInput"
                            hide-title
                            :form="valueProperty.form"
                            :required="valueProperty.required"
                            :value="periodValue"
                            :rbac-disabled="isDisabledByRbac"
                            :show-validation-errors="false"
                            @input="onValueInput"
                        />
                        <CriterionEnumInput
                            class="RecollectionIntervalCriterion__unitInput"
                            hide-title
                            :form="unitProperty.form"
                            :required="unitProperty.required"
                            :value="unit"
                            :rbac-disabled="isDisabledByRbac"
                            :show-validation-errors="false"
                            @input="onUnitInput"
                        />
                    </div>
                </RbacDisableGuard>
            </template>
            <p class="RecollectionIntervalCriterion__netNewNote">
                {{ netNewNote }}
            </p>
        </CriterionSettingsRow>
    </div>
</template>

<style lang="scss">
    .RecollectionIntervalCriterion {
        &__description {
            margin: 0 0 12px;
            font-size: 0.8rem;
            line-height: 1.4;
            color: var(--eidSecureBlue-75);
        }

        &__modes {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 12px;
        }

        &__mode {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        &__modeDescription {
            margin: 0 0 0 24px;
            font-size: var(--text-body-size, 14px);
            line-height: 1.4;
            color: var(--eidSecureBlue-75);
        }

        &__netNewNote {
            margin: 12px 0 0;
            font-size: var(--text-body-size, 14px);
            line-height: 1.4;
            color: var(--eidSecureBlue-75);
        }

        // Mirrors CriterionSettingsRow's error styling, but sized by its content instead of the fixed
        // height used there, so a message of any length stays inside the box.
        &__error {
            margin-bottom: 8px;
            padding: 10px 8px;
            font-size: 0.8rem;
            font-weight: 500;
            line-height: 1.4;
            color: var(--eidDangerRed);
            background-color: var(--eidDangerRed-10);
            border: 1px solid var(--eidDangerRed-25);
            border-radius: 4px;
        }

        &__inputs {
            display: flex;
            align-items: flex-start;
            gap: 12px;
        }

        &__valueInput,
        &__unitInput {
            flex: 1;
        }

        // The inputs are passed show-validation-errors="false" so they never own the message, which also
        // means FormInput--error never applies and neither would show any sign of the problem. Both are
        // marked, not just the period: the bound is a property of the pair, so 100 is fine for days and
        // wrong for years, and changing either field resolves it.
        &__inputs--invalid {
            .FormInput__input input {
                &, &:not([disabled]) {
                    border-color: var(--eidDangerRed);
                }
            }

            // The unit select is a Quasar q-select in outlined mode, whose border is drawn by
            // .q-field__control::before rather than on the element itself.
            .NewDropdown .q-field__control::before {
                border-color: var(--eidDangerRed);
            }
        }

        // Both inputs are driven by one shared message above the row, so their own error slots -
        // reserved by FormInput via force-error-slot - are collapsed to keep the controls level.
        .FormInput__errorMessage {
            display: none;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { defaultStrings } from '@evidentid/dashboard-commons/components/JsonSchemaForm/defaultStrings';
    import { RadioOption } from '@evidentid/dashboard-commons/components/RadioOption';
    import RbacDisableGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import createForm from '@evidentid/json-schema/createForm';
    import { JsonFormObject, JsonFormProperty } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaBasicObject } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import CriterionEnumInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/CriterionEnumInput.vue';
    import CriterionNumberInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/CriterionNumberInput.vue';
    import CriterionSettingsRow
        from '@/modules/decisioning-criteria/components/CriterionSettingsRow/CriterionSettingsRow.vue';
    import { LoadCriterionMessage } from '@/modules/decisioning-criteria/models/CriterionSettingLoadMessageFunc.model';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { requirementTypeFieldReferenceDelimiter } from '@/modules/decisioning-criteria/types';
    import {
        buildRecollectionIntervalRangeError,
        getRecollectionIntervalMode,
        getRecollectionIntervalUnit,
        RECOLLECTION_INTERVAL_MODE_PROPERTY,
        RECOLLECTION_INTERVAL_MODES,
        RECOLLECTION_INTERVAL_REFERENCE,
        RECOLLECTION_INTERVAL_UNIT_PROPERTY,
        RECOLLECTION_INTERVAL_VALUE_PROPERTY,
        RecollectionIntervalMode,
        RecollectionIntervalUnit,
    } from '@/modules/decisioning-criteria/utils/recollectionIntervalCriterion';
    import { standardizeCriterionSchema } from '@/modules/decisioning-criteria/utils/standardizeCriterionSchema';

    import {
        RECOLLECTION_INTERVAL_DESCRIPTION,
        RECOLLECTION_INTERVAL_MODE_DESCRIPTIONS,
        RECOLLECTION_INTERVAL_MODE_LABELS,
        RECOLLECTION_INTERVAL_NET_NEW_NOTE,
    } from './constants';

    type RecollectionInterval = Record<string, any>;

    // PRODUCT-26110 master-drift: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event ... but it is neither declared
    // in the emits option nor as an on[Event] prop" every time it fires. Matches the plain
    // event-name convention (not modelValue/update:modelValue) already used by the sibling
    // criterion components rendered from the same slot (EidCriterion, RiskProfileCriterion,
    // PublicProductsLiabilityCriterion) -- none of them are consumed via v-model, so `input`
    // here is just an event name, not the v-model directive.
    export default defineComponent({
        name: 'RecollectionIntervalCriterion',
        components: {
            CriterionEnumInput,
            CriterionNumberInput,
            CriterionSettingsRow,
            RadioOption,
            RbacDisableGuard,
        },
        props: {
            criterion: {
                type: Object as PropType<EditableRequirementCriterion>,
                required: true,
            },
            template: {
                type: Object as PropType<CriterionTemplate>,
                required: true,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: () => [] as CustomProperty[],
            },
            loadCriterionMessage: {
                type: Function as PropType<LoadCriterionMessage>,
                default: () => undefined,
            },
            showValidationErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'controller-value-update', 'input', 'reference-accessed' ],
        data() {
            return {
                description: RECOLLECTION_INTERVAL_DESCRIPTION,
                netNewNote: RECOLLECTION_INTERVAL_NET_NEW_NOTE,
                RbacFeature,
            };
        },
        computed: {
            mode(): RecollectionIntervalMode | null {
                return getRecollectionIntervalMode(this.intervalValue);
            },
            isRecurringCadence(): boolean {
                return this.mode === 'RECURRING_CADENCE';
            },
            // Groups the mode radios under one name so the browser treats them as a set and
            // selecting one clears the others. Built from coverageType and field - the same pair
            // that identifies this criterion in the `reference` string emitted below - since this
            // component always renders exactly one criterion's mode selector at a time.
            modeRadioGroupName(): string {
                return [ this.criterion.coverageType, this.criterion.field ]
                    .join(requirementTypeFieldReferenceDelimiter);
            },
            modeOptions(): { value: RecollectionIntervalMode, label: string, description: string }[] {
                return RECOLLECTION_INTERVAL_MODES.map((value) => ({
                    value,
                    label: RECOLLECTION_INTERVAL_MODE_LABELS[value],
                    description: RECOLLECTION_INTERVAL_MODE_DESCRIPTIONS[value],
                }));
            },
            referenceSchema(): JsonSchemaBasicObject {
                return this.template.evaluator.references[RECOLLECTION_INTERVAL_REFERENCE]
                    .schema as unknown as JsonSchemaBasicObject;
            },
            // The schema carries the unit-dependent bounds itself (as an allOf of if/then pairs), so the
            // form depends only on the template and is never rebuilt while the user types.
            intervalForm(): JsonFormObject {
                return createForm(
                    standardizeCriterionSchema(this.referenceSchema, this.customProperties),
                ) as JsonFormObject;
            },
            intervalValue(): RecollectionInterval {
                return this.criterion.references[RECOLLECTION_INTERVAL_REFERENCE] || {};
            },
            unit(): RecollectionIntervalUnit {
                return getRecollectionIntervalUnit(this.intervalValue);
            },
            valueProperty(): JsonFormProperty | null {
                return this.getIntervalProperty(RECOLLECTION_INTERVAL_VALUE_PROPERTY);
            },
            unitProperty(): JsonFormProperty | null {
                return this.getIntervalProperty(RECOLLECTION_INTERVAL_UNIT_PROPERTY);
            },
            periodValue(): number | null {
                const rawValue = this.intervalValue[RECOLLECTION_INTERVAL_VALUE_PROPERTY];
                return this.valueProperty
                    ? this.valueProperty.form.getValue(rawValue == null ? undefined : rawValue, true)
                    : null;
            },
            isPeriodEmpty(): boolean {
                const valueForm = this.valueProperty?.form;
                return valueForm ? valueForm.isEmpty(this.periodValue, true) : true;
            },
            error(): string | null {
                if (this.mode === null) {
                    return defaultStrings['error.required'];
                }
                if (this.isIntervalValid(this.intervalValue)) {
                    return null;
                }
                // Never return null for an invalid reference: the criteria list blocks Save off the
                // same validity check, so a silent null leaves the user with a dead Save button and
                // nothing to act on.
                if (!this.isRecurringCadence) {
                    return defaultStrings['error.required'];
                }
                return this.isPeriodEmpty
                    ? defaultStrings['error.required']
                    : buildRecollectionIntervalRangeError(this.referenceSchema, this.unit);
            },
            // An out of range value is reported straight away: the point of the per-unit bounds is that
            // the user learns the limit while entering the number. A missing value still waits for a
            // save attempt, so clearing the field to retype does not flash "required".
            visibleError(): string | null {
                if (!this.error) {
                    return null;
                }
                return this.showValidationErrors || !this.isPeriodEmpty ? this.error : null;
            },
        },
        created(): void {
            this.$emit('controller-value-update', this.criterion.references);
            // PRODUCT-26110 (TS2722): loadCriterionMessage is declared optional here
            // (`default: () => undefined`), matching RiskProfileCriterion.vue/
            // PublicProductsLiabilityCriterion.vue -- not EidCriterion.vue, the one
            // sibling that declares it `required: true` and calls it unguarded. Vue.extend's
            // looser prop typing masked the possibly-undefined call under Vue 2; defineComponent
            // types it correctly, surfacing TS2722. Optional invocation matches the actual
            // declared contract instead of loosening it to required.
            this.loadCriterionMessage?.(this.isIntervalValid(this.intervalValue), this.criterion);
        },
        methods: {
            getIntervalProperty(name: string): JsonFormProperty | null {
                return this.intervalForm.getProperties(this.intervalValue)
                    .find((property) => property.name === name) || null;
            },
            // Validity is only meaningful for the value and unit together, so it is always judged
            // against the object form rather than either property form.
            //
            // Judged against the resolved mode, not the stored one: a value written before
            // PRODUCT-26183 carries a period and no `mode`, which `getRecollectionIntervalMode` reads
            // as a cadence while the schema marks `mode` required. Without normalising here the two
            // disagree, and a perfectly good legacy cadence renders a range error naming bounds it
            // already satisfies while blocking Save on a criterion the user never touched. The BE
            // decoder defaults the same way rather than failing, so this is parity with it, not
            // leniency. `V518` backfills existing rows but is one-shot, so a rolling deploy can still
            // produce more of them.
            isIntervalValid(intervalValue: RecollectionInterval): boolean {
                const resolved = this.intervalForm.getValue(this.withResolvedMode(intervalValue), true);
                return this.intervalForm.isValid(resolved, true);
            },
            // An unconfigured reference resolves to no mode and is left exactly as it was, so it stays
            // invalid on the schema's required list instead of being silently completed.
            withResolvedMode(intervalValue: RecollectionInterval): RecollectionInterval {
                const mode = getRecollectionIntervalMode(intervalValue);
                return mode
                    ? { [RECOLLECTION_INTERVAL_MODE_PROPERTY]: mode, ...intervalValue }
                    : intervalValue;
            },
            onValueInput(value: number | null): void {
                this.emitIntervalUpdate({ ...this.intervalValue, [RECOLLECTION_INTERVAL_VALUE_PROPERTY]: value });
            },
            onUnitInput(unit: RecollectionIntervalUnit): void {
                this.emitIntervalUpdate({ ...this.intervalValue, [RECOLLECTION_INTERVAL_UNIT_PROPERTY]: unit });
            },
            onModeInput(mode: RecollectionIntervalMode): void {
                const next: RecollectionInterval = {
                    ...this.intervalValue,
                    [RECOLLECTION_INTERVAL_MODE_PROPERTY]: mode,
                };
                // Switching to custom keeps a usable period so switching back does not discard what
                // the user typed. An out-of-range one is dropped instead: the per-unit bounds are
                // conditioned on `unit`, not on `mode`, so keeping it would leave the reference
                // schema-invalid and block Save while the period inputs are hidden and unfixable.
                // Only the period is dropped; a lone unit stays schema-valid because the branches cap
                // `value` without requiring it, and it keeps the unit select on the user's choice.
                if (mode === 'CUSTOM_DATE_PER_SUBMISSION' && !this.isIntervalValid(next)) {
                    delete next[RECOLLECTION_INTERVAL_VALUE_PROPERTY];
                }
                this.emitIntervalUpdate(next);
            },
            emitIntervalUpdate(partialIntervalValue: RecollectionInterval): void {
                // The unit select shows the resolved unit even when nothing is stored yet, so persist it
                // alongside the first edit. Otherwise entering only a period would save a value with no
                // unit - failing the schema's required list while the form looks complete.
                // The mode is persisted for the same reason and in the same way: a legacy value has
                // its cadence inferred rather than stored, and neither `onValueInput` nor
                // `onUnitInput` writes one, so without this the stored row stays mode-less however
                // much the user edits it. `partialIntervalValue` still wins, so `onModeInput` sets it.
                const intervalValue = {
                    [RECOLLECTION_INTERVAL_UNIT_PROPERTY]: this.unit,
                    ...(this.mode ? { [RECOLLECTION_INTERVAL_MODE_PROPERTY]: this.mode } : {}),
                    ...partialIntervalValue,
                };
                const references = {
                    ...this.criterion.references,
                    [RECOLLECTION_INTERVAL_REFERENCE]: intervalValue,
                };
                const updatedCriterion: EditableRequirementCriterion = { ...this.criterion, references };
                const isValid = this.isIntervalValid(intervalValue);
                const reference = [
                    this.criterion.coverageType,
                    this.criterion.field,
                    RECOLLECTION_INTERVAL_REFERENCE,
                ].join(requirementTypeFieldReferenceDelimiter);
                this.$emit('input', updatedCriterion);
                this.$emit('reference-accessed', { reference, isValid });
                this.$emit('controller-value-update', references);
                this.loadCriterionMessage?.(isValid, updatedCriterion);
            },
        },
    });
</script>
